import { useState, useMemo, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from 'urql'
import { motion } from 'framer-motion'

import AnimatedHeading from '@/Components/UI/AnimatedHeading'
import MetaTitle from '@/Components/UI/MetaTitle'
import PseudoHeader from '@/Components/PseudoHeader/PseudoHeader'
import SummaryPageActionBar from '@/Components/SummaryPage/SummaryPageActionBar'
import EventSummaryScreen from '@/Components/SummaryPage/EventSummaryScreen'
import LoadingSpinner from '@/Components/LoadingSpinner'
import useBriefContext from '@/Hooks/Brief/useBriefContext'
import useSubmitBrief from '@/Hooks/Brief/useSubmitBrief'
import isBizBrief from '@/Logic/Form/Sequences/Biz/isBizBrief'
import { getCurrencyByCountry } from '@/Utilities/Location'
import { type LocationSelectionData } from '@/Components/PreferredLocation/LocationSection'
import { type PreviousEventRequest } from '@/Components/RepeatEvent/PreviousEventCard'
import { type PreferredLocationRouteState } from '../PreferredLocation/PreferredLocationPage'
import { StylesDocument, type StylesQuery } from '../../Styles/StylesInput'
import { useFacilitiesQuery } from '../../Facilities/Base/Facilities/FacilitiesInput.generated'
import { type DateRange } from '@/Views/FormSteps/DateTime/Utilities/getBriefDates'
import { type TimeOptionInput } from '@/Views/FormSteps/DateTime/TimePicker/TimeStamps/timeStamps'
import { type EventTypeAttributes } from '@/Views/FormSteps/EventType/Base/Fieldset'
import { clearDuplicateFormDraft } from '@/Utils/duplicateFormDraft'
import { clearNewBriefDraft } from '@/Utils/newBriefDraft'

import EventNameSection from '@/Components/DuplicateForm/EventNameSection'
import EventTypeSection from '@/Components/DuplicateForm/EventTypeSection'
import GuestCountInput from '@/Components/DuplicateForm/GuestCountInput'
import StyleSection from '@/Components/DuplicateForm/StyleSection'
import LayoutSection from '@/Components/DuplicateForm/LayoutSection'
import TimingsSection from '@/Components/DuplicateForm/DateAndTime/TimingSection'
import FacilitiesSection from '@/Components/DuplicateForm/FacilitiesSection'
import BudgetInput from '@/Components/DuplicateForm/BudgetInput'
import AdditionalNotesSection from '@/Components/DuplicateForm/AdditionalNotesSection'
import LocationSection from '@/Components/PreferredLocation/LocationSection'

interface DuplicateEventSummaryRouteState extends PreferredLocationRouteState {
  locationData: LocationSelectionData | null
}

const LAYOUT_LABELS: Record<string, string> = {
  standing: 'Standing',
  seating: 'Seated',
  classroom: 'Classroom',
  boardroom: 'Boardroom',
  cabaret: 'Cabaret',
  u_shaped: 'U-shaped',
}

const formatLayout = (layouts?: string | null): string | undefined => {
  if (!layouts || layouts === 'not_sure') return undefined
  return (
    LAYOUT_LABELS[layouts] ?? layouts.replace(/_+/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
  )
}

const getFlexibility = (
  isFlexibleOnDate?: boolean | null,
  isFlexibleOnTime?: boolean | null,
  flexibleDatesNote?: string | null
): string | undefined => {
  if (!isFlexibleOnDate && !isFlexibleOnTime) return undefined
  let msg =
    isFlexibleOnDate && isFlexibleOnTime
      ? 'Flexible on date & time'
      : isFlexibleOnDate
      ? 'Flexible on date'
      : 'Flexible on time'
  const note = flexibleDatesNote?.trim()
  if (note) msg += ` — "${note}"`
  return msg
}

const formatDate = (isoDate: string): string => {
  try {
    return new Date(isoDate).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  } catch {
    return isoDate
  }
}

const formatTime = (t: string): string => t.split(':').slice(0, 2).join(':')

interface EditFormValues {
  eventName: string
  numGuests: number | ''
  eventTypeAttrs: EventTypeAttributes
  styleIds: string[]
  layout: string | undefined
  facilityIds: number[]
  cateringNeeds: string
  budget: number | ''
  additionalNotes: string
}

const DuplicateEventSummaryPage = () => {
  const location = useLocation()
  const { brief, dispatchBrief } = useBriefContext()
  const state = (location.state ?? {}) as DuplicateEventSummaryRouteState

  const [shouldSubmitBrief, setShouldSubmitBrief] = useState(false)
  const [editMode, setEditMode] = useState(false)

  const [locationData, setLocationData] = useState<LocationSelectionData | null>(
    state.locationData ?? null
  )

  const [displayEventName, setDisplayEventName] = useState(state.eventName)
  const [displayGuests, setDisplayGuests] = useState<number | ''>(state.guests)
  const [displayDateRange, setDisplayDateRange] = useState(state.dateRange)
  const [displayTime, setDisplayTime] = useState(state.time)
  const [displayBudget, setDisplayBudget] = useState<number | ''>(state.budget)
  const [displayAdditionalNotes, setDisplayAdditionalNotes] = useState(state.additionalNotes)

  const [editModalKey, setEditModalKey] = useState(0)

  const [editDates, setEditDates] = useState<DateRange>([undefined, undefined])
  const [editStartTime, setEditStartTime] = useState<TimeOptionInput>('')
  const [editEndTime, setEditEndTime] = useState<TimeOptionInput>('')
  const [editIsFlexibleOnDate, setEditIsFlexibleOnDate] = useState(false)
  const [editIsFlexibleOnTime, setEditIsFlexibleOnTime] = useState(false)
  const [editFlexibilityNotes, setEditFlexibilityNotes] = useState('')
  const [editEventTypeCollection, setEditEventTypeCollection] = useState<string | undefined>()
  const [editLocationData, setEditLocationData] = useState<LocationSelectionData | null>(null)

  const editFormValues = useRef<EditFormValues>({
    eventName: '',
    numGuests: '',
    eventTypeAttrs: { collection: undefined, collectionName: undefined },
    styleIds: [],
    layout: undefined,
    facilityIds: [],
    cateringNeeds: '',
    budget: '',
    additionalNotes: '',
  })

  const [{ data: stylesData }] = useQuery<StylesQuery>({
    query: StylesDocument,
    context: useMemo(() => ({ requestPolicy: 'cache-first' }), []),
  })

  const [{ data: facilitiesData }] = useFacilitiesQuery({
    pause: brief.collection === undefined,
    requestPolicy: 'cache-first',
    variables: { id: brief.collection ?? '' },
  })

  const allFacilities = facilitiesData?.collection?.features ?? []
  const selectedFacilityIds = brief.spaceFacilityIds ?? brief.requiredSpaceFacilityIds ?? []
  const facilityNames = selectedFacilityIds.length
    ? selectedFacilityIds
        .map((id) => allFacilities.find((f) => parseInt(f.id, 10) === id)?.name ?? '')
        .filter(Boolean)
    : undefined

  const styleNames =
    stylesData?.getStyles && brief.styleIds?.length
      ? brief.styleIds
          .map((id) => stylesData.getStyles.find((s) => s.id === id)?.label ?? '')
          .filter(Boolean)
      : undefined

  const venueRequestsToSubmit = (state.requestedVenues ?? ([] as PreviousEventRequest[]))
    .filter((v) => v.spaceId != null && v.hostId != null)
    .map((v) => ({ spaceId: v.spaceId!, hostId: v.hostId! }))

  const venuePins = useMemo(
    () =>
      (state.requestedVenues ?? [])
        .filter((v) => v.lat != null && v.lng != null)
        .map((v) => ({ lat: v.lat!, lng: v.lng!, name: v.spaceName })),
    [state.requestedVenues]
  )

  const duplicatePrompt = state.originalEventName
    ? `This event is a duplicate of "${state.originalEventName}". 
    The user has requested the following venues, which were previously requested or booked for that original event.`
    : undefined

  const { loading: submitting } = useSubmitBrief({
    skip: !shouldSubmitBrief,
    venueRequests: venueRequestsToSubmit.length > 0 ? venueRequestsToSubmit : undefined,
    prompt: venueRequestsToSubmit.length > 0 ? duplicatePrompt : undefined,
  })

  const onConfirmAndSubmit = () => {
    if (locationData) {
      const polygonJson =
        locationData.mode === 'polygon' && locationData.polygonCoordinates
          ? JSON.stringify(locationData.polygonCoordinates)
          : undefined

      dispatchBrief({
        updateAttributes: {
          lat: locationData.lat,
          lon: locationData.lon,
          location: locationData.location,
          country: locationData.country,
          leadFeedCity: locationData.leadFeedCity,
          radius:
            locationData.mode === 'radius'
              ? { distance: locationData.radiusKm, unit: 'km' }
              : { distance: 0, unit: 'km' },
          currency:
            isBizBrief(brief) && brief.currency
              ? brief.currency
              : getCurrencyByCountry(locationData.country),
          ...(polygonJson && { locationPolygon: polygonJson }),
        },
      })
    }

    clearDuplicateFormDraft()
    clearNewBriefDraft()
    setShouldSubmitBrief(true)
  }

  const openEditMode = () => {
    const briefLayouts = brief.layouts
    const layoutValue = Array.isArray(briefLayouts)
      ? (briefLayouts[0] as string | undefined) ?? undefined
      : (briefLayouts as string | undefined) ?? undefined

    setEditDates([
      brief.fromDate ? new Date(brief.fromDate) : undefined,
      brief.toDate ? new Date(brief.toDate) : undefined,
    ])
    setEditStartTime((brief.fromTime as TimeOptionInput) ?? '')
    setEditEndTime((brief.toTime as TimeOptionInput) ?? '')
    setEditIsFlexibleOnDate(brief.isFlexibleOnDate ?? false)
    setEditIsFlexibleOnTime(brief.isFlexibleOnTime ?? false)
    setEditFlexibilityNotes(brief.flexibleDatesNote ?? '')
    setEditEventTypeCollection(brief.collection ?? undefined)
    setEditLocationData(locationData)

    editFormValues.current = {
      eventName: brief.eventName ?? displayEventName ?? '',
      numGuests: brief.capacities ?? displayGuests ?? '',
      eventTypeAttrs: {
        collection: brief.collection ?? undefined,
        collectionName: brief.collectionName ?? undefined,
      },
      styleIds: brief.styleIds ?? [],
      layout: layoutValue,
      facilityIds: [...selectedFacilityIds],
      cateringNeeds: brief.cateringNeeds ?? '',
      budget: brief.budget ?? displayBudget ?? '',
      additionalNotes: brief.additionalNotes ?? displayAdditionalNotes ?? '',
    }

    setEditModalKey((k) => k + 1)
    setEditMode(true)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const cancelEditMode = () => setEditMode(false)

  const saveEdits = () => {
    const v = editFormValues.current

    dispatchBrief({
      updateAttributes: {
        eventName: v.eventName || undefined,
        capacities: v.numGuests === '' ? undefined : Number(v.numGuests),
        ...v.eventTypeAttrs,
        styleIds: v.styleIds,
        layouts: v.layout,
        fromDate: editDates[0]?.toISOString(),
        toDate: (editDates[1] ?? editDates[0])?.toISOString(),
        fromTime: editStartTime || undefined,
        toTime: editEndTime || undefined,
        isFlexibleOnDate: editIsFlexibleOnDate,
        isFlexibleOnTime: editIsFlexibleOnTime,
        flexibleDatesNote: editFlexibilityNotes || undefined,
        spaceFacilityIds: v.facilityIds,
        cateringNeeds: v.cateringNeeds || undefined,
        budget: v.budget === '' ? undefined : Number(v.budget),
        additionalNotes: v.additionalNotes || undefined,
        ...(editLocationData?.country && {
          currency: getCurrencyByCountry(editLocationData.country),
        }),
      },
    })

    setDisplayEventName(v.eventName)
    setDisplayGuests(v.numGuests)
    setDisplayBudget(v.budget)
    setDisplayAdditionalNotes(v.additionalNotes || undefined)

    if (editDates[0]) {
      setDisplayDateRange(formatDate(editDates[0].toISOString()))
    }
    if (editStartTime && editEndTime) {
      setDisplayTime(`${formatTime(editStartTime)} - ${formatTime(editEndTime)}`)
    }

    if (editLocationData !== null) {
      setLocationData(editLocationData)
    }

    setEditMode(false)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <>
      <MetaTitle title='Review your event - HeadBox' />
      {submitting && <LoadingSpinner fullPage accessibleText='Submitting your event...' />}
      <div className={submitting ? 'hidden' : ''}>
        <PseudoHeader />
      </div>
      <div className={`mt-16 pt-6 md:pt-10 pb-36 px-4 sm:px-6 ${submitting ? 'hidden' : ''}`}>
        <div className='w-full max-w-[1120px] mx-auto py-4 sm:py-0'>
          {editMode ? (
            <div key={editModalKey} className='flex flex-col gap-8 items-start'>
              <AnimatedHeading size='large'>Edit your event details:</AnimatedHeading>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
                className='w-full bg-white border border-[#f0f0f0] rounded-[4px] px-4 py-6 flex flex-col gap-6'>
                <EventNameSection
                  savedValue={editFormValues.current.eventName}
                  onChange={(value) => {
                    editFormValues.current.eventName = value
                  }}
                />
                <EventTypeSection
                  initialEventTypeId={editFormValues.current.eventTypeAttrs.collection ?? undefined}
                  onChange={(attrs) => {
                    editFormValues.current.eventTypeAttrs = attrs
                    setEditEventTypeCollection(attrs.collection ?? undefined)
                  }}
                />
                <GuestCountInput
                  initialValue={editFormValues.current.numGuests}
                  onChange={(value) => {
                    editFormValues.current.numGuests = value
                  }}
                />
                <StyleSection
                  initialStyleIds={editFormValues.current.styleIds}
                  onChange={(ids) => {
                    editFormValues.current.styleIds = ids
                  }}
                />
                <LayoutSection
                  eventTypeCollection={editEventTypeCollection}
                  initialLayout={editFormValues.current.layout}
                  onChange={(value) => {
                    editFormValues.current.layout = value
                  }}
                />
                <TimingsSection
                  dates={editDates}
                  setDates={setEditDates}
                  startTime={editStartTime}
                  setStartTime={setEditStartTime}
                  endTime={editEndTime}
                  setEndTime={setEditEndTime}
                  isFlexibleOnDate={editIsFlexibleOnDate}
                  setIsFlexibleOnDate={setEditIsFlexibleOnDate}
                  isFlexibleOnTime={editIsFlexibleOnTime}
                  setIsFlexibleOnTime={setEditIsFlexibleOnTime}
                  flexibilityNotes={editFlexibilityNotes}
                  setFlexibilityNotes={setEditFlexibilityNotes}
                />
                <FacilitiesSection
                  eventTypeCollection={editEventTypeCollection}
                  initialFacilityIds={editFormValues.current.facilityIds}
                  initialCateringNeeds={editFormValues.current.cateringNeeds}
                  onChange={(facilityIds, cateringNeeds) => {
                    editFormValues.current.facilityIds = facilityIds
                    editFormValues.current.cateringNeeds = cateringNeeds
                  }}
                />
                <BudgetInput
                  initialValue={editFormValues.current.budget}
                  currency={brief.currency}
                  onChange={(value) => {
                    editFormValues.current.budget = value
                  }}
                />
                <AdditionalNotesSection
                  initialValue={editFormValues.current.additionalNotes}
                  onChange={(value) => {
                    editFormValues.current.additionalNotes = value
                  }}
                />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3, ease: 'easeOut' }}
                className='w-full'>
                <LocationSection
                  initialData={editLocationData ?? undefined}
                  onChange={setEditLocationData}
                  venuePins={venuePins.length ? venuePins : undefined}
                  hideHeading
                  fullWidth
                />
              </motion.div>
            </div>
          ) : (
            <EventSummaryScreen
              eventName={displayEventName}
              eventType={brief.collectionName ?? undefined}
              guests={displayGuests}
              dateRange={displayDateRange}
              time={displayTime}
              flexibility={getFlexibility(
                brief.isFlexibleOnDate,
                brief.isFlexibleOnTime,
                brief.flexibleDatesNote
              )}
              budget={displayBudget}
              currency={brief.currency}
              styles={styleNames}
              layout={formatLayout(brief.layouts as string | undefined)}
              facilities={facilityNames}
              cateringNeeds={brief.cateringNeeds ?? undefined}
              location={locationData}
              additionalNotes={displayAdditionalNotes}
              requestedVenues={state.requestedVenues?.length ? state.requestedVenues : undefined}
            />
          )}
        </div>
      </div>
      {!submitting && (
        <SummaryPageActionBar
          editMode={editMode}
          onEditDetails={openEditMode}
          onCancelEdit={cancelEditMode}
          onSaveEdits={saveEdits}
          onSubmit={onConfirmAndSubmit}
          isSubmitting={submitting}
          submitLabel='Confirm & submit'
        />
      )}
    </>
  )
}

export default DuplicateEventSummaryPage
